import type { EditRunResponse } from 'apiConnector/scheduledRun/scheduledRuns';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { VerticalFormGroup } from 'components/shared/FormGroup';
import { ConnectedSchedulesTabDisplay } from 'components/shared/ScheduledRun/Blotter/panel/Schedules/SchedulesTabDisplay';
import { ConnectedScheduleDays } from 'components/shared/ScheduledRun/ScheduleDays';
import { ConnectedScheduleRange } from 'components/shared/ScheduledRun/ScheduleRange';
import { ConnectedReadersPicker } from 'components/shared/ScheduledRun/shared/ReadersPicker';
import { TextArea } from 'components/shared/TextField/TextArea';
import { TimePicker } from 'components/shared/TimePicker';
import type { FormError } from 'models/formError';
import { useIntl } from 'react-intl';
import type { MapStateToProps } from 'react-redux';
import { connect, type MapDispatchToPropsFunction } from 'react-redux';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID, getEditableScheduledRun } from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { getScheduledFieldsErrors } from 'state/scheduledRuns/selectors';
import type { ScheduledRun, ScheduledRunFields, TriggerMode } from 'state/scheduledRuns/state';
import { TRIGGER_MODES } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import type { TypedError } from 'utils/errors/errors';
import type { LoadableContent } from 'utils/status';

interface DispatchProps {
  onTimeChanged: (time?: string) => void;
  onTriggerModeChanged: (triggerMode: TriggerMode) => void;
  onCommentChanged: (comment?: string) => void;
}
type StateProps = {
  triggerMode: TriggerMode;

  time?: string;

  errors: readonly FormError<ScheduledRunFields>[];

  timeZone: string;

  updateRun: LoadableContent<EditRunResponse | undefined, TypedError>;

  isEditing: boolean;

  comment?: string;
};

type Props = DispatchProps & StateProps;

const mapDispatchProps: MapDispatchToPropsFunction<DispatchProps, {}> = dispatch => ({
  onTimeChanged(time) {
    dispatch(scheduledRunActions.timeChanged(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, time));
  },
  onTriggerModeChanged(triggerMode) {
    dispatch(scheduledRunActions.triggerModeChanged(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, triggerMode));
  },
  onCommentChanged(comment) {
    dispatch(scheduledRunActions.commentChanged(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, comment));
  },
});

const mapStateToProps: MapStateToProps<StateProps, {}, AppState> = app => {
  const run = getEditableScheduledRun(app);
  return {
    triggerMode: run.triggerMode,
    time: run.time,
    comment: run.comment,
    errors: getScheduledFieldsErrors(app, EDITABLE_SCHEDULED_RUN_BLOTTER_ID),
    timeZone: app.scheduledRun.timeZone,
    updateRun: run?.updateRun,
    isEditing: run.type === 'editSchedule',
  };
};

function SchedulesTab({
  triggerMode,
  onTriggerModeChanged,
  onTimeChanged,
  time,
  comment,
  timeZone,
  errors,
  updateRun: { status },
  isEditing,
  onCommentChanged,
}: Props) {
  const { formatMessage } = useIntl();
  const disableForm = status === 'loading' || status === 'success';

  return isEditing ? (
    <WithInvalidFieldsContext validationErrors={errors}>
      <VerticalFormGroup<keyof ScheduledRun> inputId={'type'} label="scheduledRun.type" errorPrefix="">
        <ButtonsGroup
          isDisabled={disableForm}
          isRadio
          translationPrefix="scheduledRun.triggerType"
          items={TRIGGER_MODES}
          selectedItems={triggerMode}
          onSelectionChanged={selection => onTriggerModeChanged(selection[0])}
        />
      </VerticalFormGroup>
      {triggerMode === 'days' && (
        <ConnectedScheduleDays id={EDITABLE_SCHEDULED_RUN_BLOTTER_ID} disabled={disableForm} />
      )}
      {triggerMode === 'range' && (
        <ConnectedScheduleRange id={EDITABLE_SCHEDULED_RUN_BLOTTER_ID} disabled={disableForm} />
      )}
      <VerticalFormGroup<keyof ScheduledRunFields>
        inputId={'time'}
        labelText={formatMessage({ id: 'scheduledRun.time' }, { timeZone })}
        errorPrefix=""
      >
        <TimePicker
          type="string"
          value={time}
          disabled={disableForm}
          onValueChanged={onTimeChanged}
          format={24}
          minuteStep={15}
        />
      </VerticalFormGroup>
      <VerticalFormGroup<keyof ScheduledRunFields>
        inputId="readers"
        label="scheduledRun.distributionList"
        errorPrefix=""
      >
        <ConnectedReadersPicker disabled={disableForm} id={EDITABLE_SCHEDULED_RUN_BLOTTER_ID} />
      </VerticalFormGroup>
      <TextArea
        value={comment}
        className="form-control"
        disabled={disableForm}
        onValueChanged={onCommentChanged}
        placeholder={formatMessage({ id: 'scheduledRun.comment' })}
      />
    </WithInvalidFieldsContext>
  ) : (
    <ConnectedSchedulesTabDisplay />
  );
}

export const ConnectedSchedulesTab = connect(mapStateToProps, mapDispatchProps)(SchedulesTab);
